// Pure logic for /circle-of-fifths — the wheel's key data (signatures,
// relative minors, neighbor keys), triad inversions with played-note checking
// for the practice drills, quiz prompt builders, and localStorage stats.
// Builds on the key/chord tables in pianoDrills.js; same no-DOM/no-audio
// posture as the other music libs.

import {
  KEYS,
  MAJOR_SCALE_SEMIS,
  NOTE_NAMES_FLAT,
  NOTE_NAMES_SHARP,
  TRIAD_INTERVALS,
  chordInfo,
  chordLabel,
  chordRootPc,
  keyByName,
  voiceTriad,
  voiceTriadAscending,
} from "./pianoDrills";

/* ---------- The wheel ---------- */

// Order sharps/flats enter a key signature.
export const SHARP_ORDER = ["F♯", "C♯", "G♯", "D♯", "A♯", "E♯", "B♯"];
export const FLAT_ORDER = ["B♭", "E♭", "A♭", "D♭", "G♭", "C♭", "F♭"];

// KEYS (pianoDrills) is already in circle-of-fifths order: index 0–6 walk up
// in sharps (C..F#), 7–11 come home through the flats (Db..F).
export const WHEEL = KEYS.map((key, index) => {
  const sharps = index <= 6 ? index : 0;
  const flats = index > 6 ? 12 - index : 0;
  const names = key.useFlats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP;
  // The 6-o'clock wedge (F#, 6 sharps) sits on the enharmonic seam — it's
  // exactly as valid spelled as Gb (6 flats), and most printed wheels show
  // both. `key.name`/`minor` below stay F#/D#m everywhere (the canonical
  // spelling every other table in this file keys off of); these two extra
  // fields are display-only, derived from the flat spelling of the same
  // pitch classes rather than hardcoded.
  const enharmonic =
    sharps === 6
      ? {
          enharmonicMajor: NOTE_NAMES_FLAT[key.tonicPc],
          enharmonicMinor: `${NOTE_NAMES_FLAT[(key.tonicPc + 9) % 12]}m`,
        }
      : {};
  return {
    major: key.name,
    tonicPc: key.tonicPc,
    useFlats: key.useFlats,
    minor: `${names[(key.tonicPc + 9) % 12]}m`,
    sharps,
    flats,
    accidentals: sharps ? SHARP_ORDER.slice(0, sharps) : FLAT_ORDER.slice(0, flats),
    ...enharmonic,
  };
});

export const wheelEntry = (majorName) =>
  WHEEL.find((w) => w.major === majorName) || null;

// "3 sharps", "1 flat", "no sharps or flats".
export const signatureLabel = (entry) => {
  if (!entry) return "";
  if (entry.sharps) return `${entry.sharps} sharp${entry.sharps === 1 ? "" : "s"}`;
  if (entry.flats) return `${entry.flats} flat${entry.flats === 1 ? "" : "s"}`;
  return "no sharps or flats";
};

// Neighbors on the wheel: clockwise is up a fifth (the dominant), counter-
// clockwise down a fifth (the subdominant).
export const wheelNeighbors = (majorName) => {
  const index = WHEEL.findIndex((w) => w.major === majorName);
  if (index < 0) return null;
  return {
    dominant: WHEEL[(index + 1) % 12].major,
    subdominant: WHEEL[(index + 11) % 12].major,
  };
};

/* ---------- Brighter / darker ---------- */

// The circle's other party trick: moving one wedge clockwise (to the
// dominant) swaps exactly one scale note for a sharper one — "brighter";
// one wedge counter-clockwise (to the subdominant) swaps one note for a
// flatter one — "darker". Every other scale note is shared between
// neighboring keys, so the changed note is just the pitch-class symmetric
// difference between the two major scales: whichever pc the current key
// has that the neighbor doesn't (the note that's leaving) has a neighbor
// pc taking its place (the note that's arriving), and since a major scale
// covers 7 of 12 pcs, exactly one pc differs each way.
export const brightnessStep = (majorName, direction) => {
  const entry = wheelEntry(majorName);
  const neighbors = wheelNeighbors(majorName);
  if (!entry || !neighbors) return null;
  const neighborName = direction === "brighter" ? neighbors.dominant : neighbors.subdominant;
  const neighborEntry = wheelEntry(neighborName);
  if (!neighborEntry) return null;

  const scalePcs = (tonicPc) => MAJOR_SCALE_SEMIS.map((s) => (tonicPc + s) % 12);
  const currentPcs = scalePcs(entry.tonicPc);
  const neighborPcSet = new Set(scalePcs(neighborEntry.tonicPc));

  // The one degree (1-7) of the current scale whose pc the neighbor lacks —
  // that's the note being replaced.
  const degreeIndex = currentPcs.findIndex((pc) => !neighborPcSet.has(pc));
  const droppedPc = currentPcs[degreeIndex];
  // The one pc the neighbor has that the current scale lacks — the note
  // taking its place, spelled the way the neighbor key spells it. Spelling
  // each note in its own key's accidentals (rather than one shared
  // spelling) is what makes the F#/Db seam come out right: F# major's B
  // becomes Db major's C — same pitch class, respelled going brighter.
  const addedPc = [...neighborPcSet].find((pc) => !currentPcs.includes(pc));

  const currentNames = entry.useFlats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP;
  const neighborNames = neighborEntry.useFlats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP;

  return {
    key: neighborName,
    from: currentNames[droppedPc],
    to: neighborNames[addedPc],
    degree: degreeIndex + 1,
  };
};

// The seven modes that share a home tonic, brightest to darkest. Each one is
// "the neighboring key's notes, played from the same home note" — Lydian
// borrows the dominant's notes, Mixolydian the subdominant's, and so on one
// more wedge counter-clockwise per row down to Locrian five wedges out.
// `offset` is wedges clockwise from the home major to the key being
// borrowed from.
export const MODE_LADDER = [
  { mode: "Lydian", offset: 1 },
  { mode: "Ionian", offset: 0 },
  { mode: "Mixolydian", offset: -1 },
  { mode: "Dorian", offset: -2 },
  { mode: "Aeolian", offset: -3 },
  { mode: "Phrygian", offset: -4 },
  { mode: "Locrian", offset: -5 },
];

// `entry`'s major scale, spelled in its own accidentals, rotated to start on
// `tonicPc` — some other key's tonic that happens to be one of this scale's
// seven pitch classes (true for every row modeLadder builds).
const rotatedScaleNotes = (entry, tonicPc) => {
  const names = entry.useFlats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP;
  const pcs = MAJOR_SCALE_SEMIS.map((s) => (entry.tonicPc + s) % 12);
  const start = pcs.indexOf(tonicPc);
  const rotated = [...pcs.slice(start), ...pcs.slice(0, start)];
  return rotated.map((pc) => names[pc]);
};

// The full brightness ladder for a home key: seven rows, brightest (Lydian)
// to darkest (Locrian), each naming the neighboring key its notes are
// borrowed from and — save for the first row — the single note that got
// lower than the row above it. Consecutive rows are always one wedge apart
// (each offset is one less than the last), so that single-note change is
// just brightnessStep's "darker" move from the previous row's source key —
// same symmetric-difference logic, reused rather than re-derived.
export const modeLadder = (majorName) => {
  const homeIndex = WHEEL.findIndex((w) => w.major === majorName);
  if (homeIndex < 0) return null;
  const homeTonicPc = WHEEL[homeIndex].tonicPc;
  let prevSourceKey = null;
  return MODE_LADDER.map(({ mode, offset }) => {
    const sourceEntry = WHEEL[(homeIndex + offset + 12) % 12];
    const sourceKey = sourceEntry.major;
    const notes = rotatedScaleNotes(sourceEntry, homeTonicPc);
    const step = prevSourceKey ? brightnessStep(prevSourceKey, "darker") : null;
    const changed = step ? { from: step.from, to: step.to } : null;
    prevSourceKey = sourceKey;
    return { mode, offset, sourceKey, tonic: notes[0], notes, changed };
  });
};

/* ---------- Relative minor perspective ---------- */

// Natural minor.
export const MINOR_SCALE_SEMIS = [0, 2, 3, 5, 7, 8, 10];

// Minor degree 1..7's diatonic chord — same chords as the relative major's
// diatonic triads, just renumbered from the minor tonic. majorDegree maps a
// minor degree onto the major scale degree that lands on the same chord
// (minor's i is major's vi, and so on around the circle).
export const MINOR_DIATONIC = [
  { degree: 1, roman: "i", quality: "min", majorDegree: 6 },
  { degree: 2, roman: "ii°", quality: "dim", majorDegree: 7 },
  { degree: 3, roman: "III", quality: "maj", majorDegree: 1 },
  { degree: 4, roman: "iv", quality: "min", majorDegree: 2 },
  { degree: 5, roman: "v", quality: "min", majorDegree: 3 },
  { degree: 6, roman: "VI", quality: "maj", majorDegree: 4 },
  { degree: 7, roman: "VII", quality: "maj", majorDegree: 5 },
];

// Display label for a minor-degree chord ("Am", "B°", ...): the same chord
// as the relative major's corresponding degree, so this just borrows
// chordLabel via the majorDegree mapping above.
export const minorChordLabel = (majorKeyName, minorDegree) => {
  const info = MINOR_DIATONIC[minorDegree - 1];
  if (!info) return "";
  return chordLabel(majorKeyName, info.majorDegree);
};

// Anchored voicing from the MINOR tonic: the minor tonic's last stop below
// middle C (mirroring voiceTriadAscending's `preferred` anchor, but rooted a
// minor third below the major tonic), with degrees climbing the natural
// minor scale from there. Null for a bad key or degree.
export const anchoredMinorTriad = (majorKeyName, minorDegree) => {
  const entry = wheelEntry(majorKeyName);
  const info = MINOR_DIATONIC[minorDegree - 1];
  if (!entry || !info) return null;
  const minorTonicRoot = 48 + ((entry.tonicPc + 9) % 12);
  const root = minorTonicRoot + MINOR_SCALE_SEMIS[minorDegree - 1];
  return TRIAD_INTERVALS[info.quality].map((i) => root + i);
};

// The seven scale notes, spelled with the key's accidentals, from either
// perspective: the major tonic and MAJOR_SCALE_SEMIS, or the relative minor
// tonic and MINOR_SCALE_SEMIS.
export const scaleNotes = (majorKeyName, perspective) => {
  const entry = wheelEntry(majorKeyName);
  if (!entry) return [];
  const names = entry.useFlats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP;
  const minor = perspective === "minor";
  const tonicPc = minor ? (entry.tonicPc + 9) % 12 : entry.tonicPc;
  const semis = minor ? MINOR_SCALE_SEMIS : MAJOR_SCALE_SEMIS;
  return semis.map((s) => names[(tonicPc + s) % 12]);
};

/* ---------- Inversions ---------- */

export const INVERSIONS = [
  { id: 0, label: "root position" },
  { id: 1, label: "1st inversion" },
  { id: 2, label: "2nd inversion" },
];

// Rotate a low-to-high chord voicing (any size) up `inversion` times: take
// the lowest note, drop it an octave higher, repeat. Shared by triads and
// sevenths below.
export const invertChord = (midis, inversion) => {
  let out = [...midis];
  for (let i = 0; i < inversion; i += 1) out = [...out.slice(1), out[0] + 12];
  return out;
};

// Rotate a low-to-high triad voicing up `inversion` times.
export const invertTriad = (midis, inversion) => invertChord(midis, inversion);

// The triad's pitch classes low-to-high in root position; index by inversion
// to get the expected bass pitch class.
export const triadPcs = (keyName, degree) => {
  const midis = voiceTriad(keyName, degree, 36, 96);
  return midis ? midis.map((m) => ((m % 12) + 12) % 12) : null;
};

// Anchored voicing for this page: the climbing voicing over the page's fixed
// C3–C6 keyboard, which lands every key's tonic root just below middle C
// (48 + tonicPc) so walking up the degrees never wraps down an octave.
export const anchoredTriad = (keyName, degree) =>
  voiceTriadAscending(keyName, degree, 48, 84);

// A displayable voicing of the inversion that fits the given range: the
// anchored root position, rotated upward.
export const targetInversionMidis = (keyName, degree, inversion, low, high) => {
  const base = anchoredTriad(keyName, degree);
  if (!base) return null;
  let midis = invertTriad(base, inversion);
  while (midis[midis.length - 1] > high) midis = midis.map((m) => m - 12);
  while (midis[0] < low) midis = midis.map((m) => m + 12);
  return midis[midis.length - 1] <= high ? midis : null;
};

// Which inversion of "degree's triad" a set of held notes is: 0/1/2 when the
// pitch classes match the triad (any octaves), keyed off the lowest note;
// null when the notes aren't that chord at all.
export const detectInversion = (playedMidis, keyName, degree) => {
  const pcs = triadPcs(keyName, degree);
  if (!pcs || !playedMidis || playedMidis.length < 3) return null;
  const want = new Set(pcs);
  const got = new Set(playedMidis.map((m) => ((m % 12) + 12) % 12));
  const sameNotes = got.size === want.size && [...want].every((pc) => got.has(pc));
  if (!sameNotes) return null;
  const bassPc = ((Math.min(...playedMidis) % 12) + 12) % 12;
  return pcs.indexOf(bassPc); // bass is one of the matched pcs, so never -1
};

// Judge a set of held notes against "degree's triad in this inversion":
// octave-agnostic on everything except which pitch class is on the bottom.
// Returns { correct, reason: null | "wrongNotes" | "wrongBass" }.
export const checkInversion = (playedMidis, keyName, degree, inversion) => {
  const found = detectInversion(playedMidis, keyName, degree);
  if (found == null) return { correct: false, reason: "wrongNotes" };
  if (found !== inversion) return { correct: false, reason: "wrongBass" };
  return { correct: true, reason: null };
};

// The bass note name for degree's triad in this inversion, spelled with the
// key's accidentals ("E" for C's 1st inversion). Shared by inversionChordLabel
// and the minor-perspective drill/learn code below.
export const inversionBassName = (keyName, degree, inversion) => {
  const key = keyByName(keyName);
  const pcs = triadPcs(keyName, degree);
  if (!key || !pcs) return "";
  const names = key.useFlats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP;
  return names[pcs[inversion]];
};

// "D/F♯"-style label; plain chord label in root position.
export const inversionChordLabel = (keyName, degree, inversion) => {
  const info = chordInfo(degree);
  const pcs = triadPcs(keyName, degree);
  if (!info || !pcs) return "";
  const base = chordLabel(keyName, degree);
  if (!inversion) return base;
  return `${base}/${inversionBassName(keyName, degree, inversion)}`;
};

/* ---------- Seventh chords ---------- */

// Diatonic seventh-chord quality per major-key degree 1..7, stacking one
// more diatonic third on the triad: maj7, m7, m7, maj7, dom7 (the V7 that
// gives the wheel its "pulls home" drill), m7, half-diminished on vii°.
export const SEVENTH_QUALITIES = {
  maj7: { suffix: "maj7", name: "major seventh" },
  m7: { suffix: "m7", name: "minor seventh" },
  dom7: { suffix: "7", name: "dominant seventh" },
  halfDim: { suffix: "ø7", name: "half-diminished seventh" },
};

const SEVENTH_QUALITY_BY_DEGREE = ["maj7", "m7", "m7", "maj7", "dom7", "m7", "halfDim"];

export const seventhQuality = (degree) => SEVENTH_QUALITY_BY_DEGREE[degree - 1] || null;

// "Cmaj7", "Dm7", ..., "Bø7" — same root-naming as chordLabel, with the
// seventh's suffix swapped in.
export const seventhLabel = (keyName, degree) => {
  const key = keyByName(keyName);
  const quality = seventhQuality(degree);
  const rootPc = chordRootPc(keyName, degree);
  if (!key || !quality || rootPc == null) return "";
  const names = key.useFlats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP;
  return `${names[rootPc]}${SEVENTH_QUALITIES[quality].suffix}`;
};

// Minor-degree seventh label, borrowed from the relative major the same way
// minorChordLabel borrows triads: remap through MINOR_DIATONIC's majorDegree
// rather than keeping a second quality table.
export const minorSeventhLabel = (keyName, minorDegree) => {
  const info = MINOR_DIATONIC[minorDegree - 1];
  if (!info) return "";
  return seventhLabel(keyName, info.majorDegree);
};

// Anchored seventh voicing: anchoredTriad's root/third/fifth, plus one more
// diatonic third stacked on top. Re-derives the octave anchor
// (voiceTriadAscending's internal `best.tonic`) from the triad's own root
// rather than re-running that search, then walks two more scale steps up
// the major scale (offset 6 from the degree) for the seventh.
export const anchoredSeventh = (keyName, degree) => {
  const triad = anchoredTriad(keyName, degree);
  if (!triad) return null;
  const d0 = degree - 1;
  const tonic = triad[0] - MAJOR_SCALE_SEMIS[d0];
  const step = d0 + 6;
  const seventh = tonic + MAJOR_SCALE_SEMIS[step % 7] + 12 * Math.floor(step / 7);
  return [...triad, seventh];
};

// Mirror of anchoredSeventh, rooted on the relative minor (natural minor
// scale-walk) instead of the major.
export const anchoredMinorSeventh = (majorKeyName, minorDegree) => {
  const triad = anchoredMinorTriad(majorKeyName, minorDegree);
  if (!triad) return null;
  const d0 = minorDegree - 1;
  const tonic = triad[0] - MINOR_SCALE_SEMIS[d0];
  const step = d0 + 6;
  const seventh = tonic + MINOR_SCALE_SEMIS[step % 7] + 12 * Math.floor(step / 7);
  return [...triad, seventh];
};

export const SEVENTH_INVERSIONS = [
  { id: 0, label: "root position" },
  { id: 1, label: "1st inversion" },
  { id: 2, label: "2nd inversion" },
  { id: 3, label: "3rd inversion" },
];

// The seventh chord's pitch classes low-to-high in root position; index by
// inversion to get the expected bass pitch class. Mirrors triadPcs, but
// there's no pianoDrills range-voicer for four-note chords, so this reads
// off anchoredSeventh instead of a fresh voiceSeventh.
export const seventhPcs = (keyName, degree) => {
  const midis = anchoredSeventh(keyName, degree);
  return midis ? midis.map((m) => ((m % 12) + 12) % 12) : null;
};

// Mirror of targetInversionMidis for four-note chords.
export const targetSeventhInversionMidis = (keyName, degree, inversion, low, high) => {
  const base = anchoredSeventh(keyName, degree);
  if (!base) return null;
  let midis = invertChord(base, inversion);
  while (midis[midis.length - 1] > high) midis = midis.map((m) => m - 12);
  while (midis[0] < low) midis = midis.map((m) => m + 12);
  return midis[midis.length - 1] <= high ? midis : null;
};

// Mirror of targetMinorInversionMidis for four-note chords.
export const targetMinorSeventhInversionMidis = (keyName, minorDegree, inversion, low, high) => {
  const base = anchoredMinorSeventh(keyName, minorDegree);
  if (!base) return null;
  let midis = invertChord(base, inversion);
  while (midis[midis.length - 1] > high) midis = midis.map((m) => m - 12);
  while (midis[0] < low) midis = midis.map((m) => m + 12);
  return midis[midis.length - 1] <= high ? midis : null;
};

// Mirror of detectInversion for four-note chords: which inversion (0-3) of
// "degree's seventh" a set of held notes is, keyed off the lowest note;
// null when the played pitch classes aren't an exact match (missing a
// chord tone, or an extra note that isn't one).
export const detectSeventhInversion = (playedMidis, keyName, degree) => {
  const pcs = seventhPcs(keyName, degree);
  if (!pcs || !playedMidis || playedMidis.length < 4) return null;
  const want = new Set(pcs);
  const got = new Set(playedMidis.map((m) => ((m % 12) + 12) % 12));
  const sameNotes = got.size === want.size && [...want].every((pc) => got.has(pc));
  if (!sameNotes) return null;
  const bassPc = ((Math.min(...playedMidis) % 12) + 12) % 12;
  return pcs.indexOf(bassPc); // bass is one of the matched pcs, so never -1
};

// Mirror of inversionBassName for four-note chords.
export const seventhBassName = (keyName, degree, inversion) => {
  const key = keyByName(keyName);
  const pcs = seventhPcs(keyName, degree);
  if (!key || !pcs) return "";
  const names = key.useFlats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP;
  return names[pcs[inversion]];
};

// "G7"-style label; slash chord ("G7/B") for inversions 1-3.
export const seventhSlashLabel = (keyName, degree, inversion) => {
  const base = seventhLabel(keyName, degree);
  if (!base) return "";
  if (!inversion) return base;
  return `${base}/${seventhBassName(keyName, degree, inversion)}`;
};

// Total motion between two same-size chords, compared note-to-note after
// sorting both low to high.
const totalMotion = (a, b) => {
  const sortedA = [...a].sort((x, y) => x - y);
  const sortedB = [...b].sort((x, y) => x - y);
  return sortedA.reduce((sum, midi, i) => sum + Math.abs(midi - sortedB[i]), 0);
};

// Every octave-shifted copy of a chord that still fits inside [low, high].
const chordOctaveShiftsInRange = (midis, low, high) => {
  const shifts = [];
  for (let oct = -8; oct <= 8; oct += 1) {
    const shifted = midis.map((m) => m + oct * 12);
    if (shifted[0] >= low && shifted[shifted.length - 1] <= high) shifts.push(shifted);
  }
  return shifts;
};

// Voice-leading picker for the "one-finger trick" demo and the ii–V–I trip:
// among all four inversions of degree's seventh chord, at every octave shift
// that fits [low, high], return whichever candidate moves the least (summed
// note-to-note distance) from prevMidis. With no previous chord, just fit
// the anchored root position into range.
export const smoothSeventh = (prevMidis, keyName, degree, low, high) => {
  if (!prevMidis || !prevMidis.length) {
    return targetSeventhInversionMidis(keyName, degree, 0, low, high);
  }
  const base = anchoredSeventh(keyName, degree);
  if (!base) return null;
  let best = null;
  SEVENTH_INVERSIONS.forEach(({ id }) => {
    const inverted = invertChord(base, id);
    chordOctaveShiftsInRange(inverted, low, high).forEach((candidate) => {
      const motion = totalMotion(prevMidis, candidate);
      if (!best || motion < best.motion) best = { candidate, motion };
    });
  });
  return best ? best.candidate : null;
};

// The ii–V–I in this key, voice-led: ii is the anchored seventh fit into
// range; V and I are smoothSeventh-chained off the chord before them.
export const iiVIChords = (keyName, low, high) => {
  const ii = targetSeventhInversionMidis(keyName, 2, 0, low, high);
  const v = smoothSeventh(ii, keyName, 5, low, high);
  const i = smoothSeventh(v, keyName, 1, low, high);
  return [
    { degree: 2, label: seventhLabel(keyName, 2), midis: ii },
    { degree: 5, label: seventhLabel(keyName, 5), midis: v },
    { degree: 1, label: seventhLabel(keyName, 1), midis: i },
  ];
};

// Random seventh-chord inversion prompt, mirroring randomInversionPrompt but
// over all seven degrees (ø7 included) and all four inversions.
export const randomSeventhPrompt = (keyName, rng = Math.random, perspective = "major") => {
  if (perspective === "minor") {
    const minorDegree = 1 + Math.floor(rng() * 7);
    const degree = MINOR_DIATONIC[minorDegree - 1].majorDegree;
    const inversion = Math.floor(rng() * 4);
    return {
      keyName,
      perspective: "minor",
      degree,
      minorDegree,
      chord: minorSeventhLabel(keyName, minorDegree),
      slashChord: seventhSlashLabel(keyName, degree, inversion),
      inversion,
      inversionLabel: SEVENTH_INVERSIONS[inversion].label,
    };
  }
  const degree = 1 + Math.floor(rng() * 7);
  const inversion = Math.floor(rng() * 4);
  return {
    keyName,
    perspective: "major",
    degree,
    minorDegree: null,
    chord: seventhLabel(keyName, degree),
    slashChord: seventhSlashLabel(keyName, degree, inversion),
    inversion,
    inversionLabel: SEVENTH_INVERSIONS[inversion].label,
  };
};

// "Which key does this pull toward?" drill: a random wheel key's V7 —
// answering it names the key that dominant resolves to.
export const randomDominantPrompt = (rng = Math.random) => {
  const entry = WHEEL[Math.floor(rng() * WHEEL.length)];
  return {
    answer: entry.major,
    chord: seventhLabel(entry.major, 5),
    midis: anchoredSeventh(entry.major, 5),
  };
};

/* ---------- Drill prompts ---------- */

// Minor degrees eligible for inversion drilling — ii° sits out, same reason
// vii° does in the major path below.
export const MINOR_INVERSION_DEGREES = [1, 3, 4, 5, 6, 7];

// Random inversion prompt in a key, from either perspective. Major: degrees
// 1–6 — vii° is a rough place to learn inversions, so it sits out. Minor:
// MINOR_INVERSION_DEGREES, remapped through MINOR_DIATONIC's majorDegree so
// `degree` (and everything downstream that judges against it — checkInversion,
// triadPcs) stays untouched; `minorDegree` carries the minor-numbered degree
// for display.
export const randomInversionPrompt = (keyName, rng = Math.random, perspective = "major") => {
  if (perspective === "minor") {
    const minorDegree = MINOR_INVERSION_DEGREES[Math.floor(rng() * 6)];
    const degree = MINOR_DIATONIC[minorDegree - 1].majorDegree;
    const inversion = Math.floor(rng() * 3);
    return {
      keyName,
      degree,
      inversion,
      chord: minorChordLabel(keyName, minorDegree),
      slashChord: inversionChordLabel(keyName, degree, inversion),
      inversionLabel: INVERSIONS[inversion].label,
      perspective: "minor",
      minorDegree,
    };
  }
  const degree = 1 + Math.floor(rng() * 6);
  const inversion = Math.floor(rng() * 3);
  return {
    keyName,
    degree,
    inversion,
    chord: chordLabel(keyName, degree),
    slashChord: inversionChordLabel(keyName, degree, inversion),
    inversionLabel: INVERSIONS[inversion].label,
    perspective: "major",
    minorDegree: null,
  };
};

// Mirror of targetInversionMidis, anchored on the relative minor's triad
// instead of the major's.
export const targetMinorInversionMidis = (keyName, minorDegree, inversion, low, high) => {
  const base = anchoredMinorTriad(keyName, minorDegree);
  if (!base) return null;
  let midis = invertTriad(base, inversion);
  while (midis[midis.length - 1] > high) midis = midis.map((m) => m - 12);
  while (midis[0] < low) midis = midis.map((m) => m + 12);
  return midis[midis.length - 1] <= high ? midis : null;
};

/* ---------- Degree badges on the wheel ---------- */

// The wheel's party trick: a key's diatonic chords are all wedge-neighbors of
// its slice. With C selected — I on C, vi on Am under it; V on G, iii on Em
// under it; IV on F, ii on Dm under it; and vii° one more step clockwise on
// Bm's wedge (same letter, diminished quality). Returns { majorName: { outer?,
// inner? } } of small roman-numeral labels; minor perspective renumbers the
// same wedges from the relative minor's point of view.
export const wheelDegreeBadges = (majorKeyName, perspective = "major") => {
  const index = WHEEL.findIndex((w) => w.major === majorKeyName);
  if (index < 0) return {};
  const at = (offset) => WHEEL[(index + offset + 12) % 12].major;
  const minor = perspective === "minor";
  return {
    [at(0)]: minor ? { outer: "III", inner: "i" } : { outer: "I", inner: "vi" },
    [at(1)]: minor ? { outer: "VII", inner: "v" } : { outer: "V", inner: "iii" },
    [at(11)]: minor ? { outer: "VI", inner: "iv" } : { outer: "IV", inner: "ii" },
    [at(2)]: minor ? { inner: "ii°" } : { inner: "vii°" },
  };
};

// Random find-it-on-the-wheel prompt: name a key by its signature or by its
// relative minor; the wheel itself is the answer surface.
export const randomWheelPrompt = (rng = Math.random) => {
  const entry = WHEEL[Math.floor(rng() * WHEEL.length)];
  const byMinor = rng() < 0.5;
  return {
    answer: entry.major,
    question: byMinor
      ? `Which major key is ${entry.minor} the relative minor of?`
      : `Which major key has ${signatureLabel(entry)}?`,
  };
};

/* ---------- Stats (localStorage) ---------- */

export const STATS_KEY = "circleOfFifthsStats";
const EMPTY_MODE = { attempts: 0, correct: 0, streak: 0, best: 0 };

export const emptyStats = () => ({
  inversions: { ...EMPTY_MODE },
  wheel: { ...EMPTY_MODE },
  sevenths: { ...EMPTY_MODE },
});

// Merge saved data over emptyStats() bucket by bucket, so a localStorage
// blob from before a new mode existed (e.g. no "sevenths" key yet) still
// comes back with every bucket present.
export const loadStats = () => {
  try {
    const raw = window.localStorage.getItem(STATS_KEY);
    if (!raw) return emptyStats();
    const parsed = JSON.parse(raw);
    const empty = emptyStats();
    return {
      inversions: { ...empty.inversions, ...(parsed.inversions || {}) },
      wheel: { ...empty.wheel, ...(parsed.wheel || {}) },
      sevenths: { ...empty.sevenths, ...(parsed.sevenths || {}) },
    };
  } catch (err) {
    return emptyStats();
  }
};

export const saveStats = (stats) => {
  try {
    window.localStorage.setItem(STATS_KEY, JSON.stringify(stats));
  } catch (err) {
    // Private mode / quota — stats just don't persist.
  }
};

// Pure update: returns a new stats object with one result folded in.
export const recordResult = (stats, mode, correct) => {
  const prev = (stats && stats[mode]) || EMPTY_MODE;
  const streak = correct ? prev.streak + 1 : 0;
  return {
    ...(stats || emptyStats()),
    [mode]: {
      attempts: prev.attempts + 1,
      correct: prev.correct + (correct ? 1 : 0),
      streak,
      best: Math.max(prev.best, streak),
    },
  };
};
